/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { FunctionComponent, useContext } from 'react';
import { Box, IconButton, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import HeartSmall from 'wix-ui-icons-common/HeartSmall';
import HeartFilledSmall from 'wix-ui-icons-common/HeartFilledSmall';
import ThumbUpSmall from 'wix-ui-icons-common/ThumbsUpSmall';
import ThumbUpFilledSmall from 'wix-ui-icons-common/ThumbsUpFilledSmall';
import { ArticleResolution, FeatureRequestResolutions } from '@wix/answers-api';
import { DATA_HOOKS } from '../../../dataHooks';
import { ArticleSearchResultItem } from '../../../types';
import { Context } from '../../../context';
import { statusToClass, launchedTagText } from '../../../utils/roadmap';
import css from './index.module.scss';
import { isMobile } from '../../../utils/userAgent';
import { COLORS } from './../../../utils/theme';
import { RoadmapResolutionEta } from '../../RoadmapResolutionEta';

type RoadmapArticleListItemFooterProps = {
  article: ArticleSearchResultItem;
  followArticle: (articleId: string) => void;
  unfollowArticle: (articleId: string) => void;
  isFollowing: boolean;
  onClickStatus: (resolution: ArticleResolution) => void;
};
export const LABELS_LENGTH = 3;

export const MAX_PREVIEW_SIZE = 300;

export const RoadmapArticleListItemFooter: FunctionComponent<
  RoadmapArticleListItemFooterProps
> = ({
  article,
  followArticle,
  unfollowArticle,
  isFollowing,
  onClickStatus,
}: RoadmapArticleListItemFooterProps) => {
  const { t } = useTranslation();
  const context = useContext(Context);
  const isMobileView = isMobile(context);

  const clickFollowArticle = () => followArticle(article.id);
  const clickUnfollowArticle = () => unfollowArticle(article.id);

  const mobileHeartIcon = isFollowing ? (
    <HeartFilledSmall color={COLORS.B10} />
  ) : (
    <HeartSmall className={css.mobileHeartIcon} color={COLORS.B10} />
  );

  const thumbUpIcon = isFollowing ? (
    <ThumbUpFilledSmall color={COLORS.B10} />
  ) : (
    <ThumbUpSmall className={css.mobileHeartIcon} color={COLORS.B10} />
  );

  const mobileFollowButton = (
    <IconButton
      className={
        isFollowing ? css.mobileFollowedButton : css.mobileFollowButton
      }
      priority={isFollowing ? 'primary' : 'secondary'}
      onClick={isFollowing ? clickUnfollowArticle : clickFollowArticle}
      size="small"
    >
      {article.resolution === ArticleResolution.COLLECTING_VOTES
        ? thumbUpIcon
        : mobileHeartIcon}
    </IconButton>
  );

  const resolution =
    statusToClass[
      (article.resolution as unknown as FeatureRequestResolutions) ||
        ArticleResolution.COLLECTING_VOTES
    ];

  const renderEta = (eta: number) => {
    const etaDate = new Date(eta);
    const month = etaDate.getMonth();
    const year = etaDate.getFullYear();
    const quarter = Math.ceil((month + 1) / 3);
    return (
      <Text size="small" className={css.eta}>
        {t('roadmap.article-item.eta', { quarter, year })}
      </Text>
    );
  };

  const statusBody = (
    <div className={`${css.statusBody}`}>
      <Text
        dataHook={DATA_HOOKS.ARTICLE_ITEM_STATUS}
        datatype={`${article.resolution}`}
        size="small"
        className={`${css.statusText} ${css[resolution]}`}
      >
        {article.resolution === ArticleResolution.RELEASED &&
        article.resolutionEta
          ? launchedTagText(article.resolutionEta, t)
          : t(`roadmap.article-list.resolutions.${article.resolution}`)}
      </Text>
    </div>
  );

  const mobileFooter = (
    <Box
      className={css.mobileFooter}
      align="space-between"
      direction="horizontal"
      alignItems="center"
      verticalAlign="middle"
    >
      <Box verticalAlign="middle" className={css.mobileTagsWrapper}>
        {article.resolution && statusBody}
        {article.resolutionEta &&
          article.resolution !== ArticleResolution.RELEASED && (
            <Text className={css.spacingDot}>·</Text>
          )}
        {article.resolutionEta &&
          article.resolution !== ArticleResolution.RELEASED &&
          renderEta(article.resolutionEta)}
      </Box>
      {article.resolution !== ArticleResolution.RELEASED && mobileFollowButton}
    </Box>
  );

  const footer = (
    <div className={`${css.tagsWrapper}`}>
      <RoadmapResolutionEta
        resolution={article.resolution}
        resolutionEta={article.resolutionEta || undefined}
        onClickStatus={onClickStatus}
      />
    </div>
  );

  return isMobileView ? mobileFooter : footer;
};
